/**
 * Pure geometry/style decisions for the in-game attack/fortify selection mark.
 *
 * The map renderer owns the SVG and animation; keeping the decision here makes
 * the important invariants testable without importing react-native-svg.
 */

export type SelectionEmphasisRole = 'source' | 'destination'
export type SelectionEmphasisTheme = 'light' | 'dark'

export const SELECTION_ACCENT: Record<SelectionEmphasisTheme, string> = {
  light: '#D400BE',
  dark: '#FF3DE8',
}

export const SELECTION_CASING = '#14100C'

export interface SelectionEmphasisInput {
  role: SelectionEmphasisRole
  selectedFillMode: 'amber' | 'lighten'
  showRegionBonuses: boolean
  theme: SelectionEmphasisTheme
  internalBorderWidth: number
  badgeRadius: number
  reducedMotion: boolean
}

export interface SelectionEmphasisStyle {
  accent: string
  casing: string
  outlineWidths: { casing: number; accent: number }
  ringRadius: number
  ringWidths: { casing: number; accent: number }
  opacities: { casing: number; accent: number }
  /** One-shot exterior halo dimensions and resting reduced-motion opacity. */
  glow: { strokeWidth: number; opacity: number }
  /** True only for the source's one-shot arrival animation. */
  animate: boolean
  /** True for the one-shot exterior glow on either selected territory. */
  glowAnimate: boolean
}

/**
 * Return the play-mode selection treatment, or null for the inspector and
 * regions surfaces. The phase is intentionally absent from this input: attack
 * and fortify must use byte-identical selection geometry and colour.
 */
export function selectionEmphasis(
  input: SelectionEmphasisInput,
): SelectionEmphasisStyle | null {
  if (input.selectedFillMode !== 'lighten' || input.showRegionBonuses) return null

  const weight = input.role === 'source' ? 1 : 0.72
  const safeBorderWidth = Math.max(0, input.internalBorderWidth)
  const safeBadgeRadius = Math.max(0, input.badgeRadius)

  return {
    accent: SELECTION_ACCENT[input.theme],
    casing: SELECTION_CASING,
    outlineWidths: {
      casing: Math.max(0.8, safeBorderWidth * 3 * weight),
      // The source outline replaces the old magenta accent, so make it
      // unmistakably thicker than the territory's resting border. The
      // destination stays secondary through its lower source weight.
      accent: Math.max(0.45, safeBorderWidth * 2.4 * weight),
    },
    ringRadius: safeBadgeRadius * 1.35,
    ringWidths: {
      casing: Math.max(0.7, safeBadgeRadius * 0.18 * weight),
      // The badge is the scale-independent part of the mark. Keep its
      // magenta stroke at full source width even for destinations; reducing
      // this ring made targets disappear on crimson fills. The destination
      // remains secondary through its polygon outline and casing opacity.
      accent: Math.max(0.4, safeBadgeRadius * 0.09),
    },
    opacities: {
      casing: input.role === 'source' ? 1 : 0.82,
      // Keep the destination's geometry lighter than the source. The renderer
      // supplies white for the destination accent so the target remains
      // hue-free over every player fill.
      accent: 1,
    },
    glow: {
      strokeWidth: Math.max(1.2, safeBorderWidth * 6 * weight),
      opacity: input.role === 'source' ? 0.42 : 0.28,
    },
    animate: input.role === 'source' && !input.reducedMotion,
    glowAnimate: !input.reducedMotion,
  }
}
